import {
  V1ActionsPerformedOnDayResponse,
  V1ResourceActionsDailyResponse,
} from '@ariksa/audittrail';
import {
  V1ActionsPerformedMonthlyResponse,
  V1ResourceActionsMonthlyResponse,
} from '@ariksa/audittrail/api';
import { CloudProviders } from '@ariksa/cloud-account/api';
import {
  S3CrossAccountAccess,
  S3InsecureACL,
  S3InternetActivity,
} from '@ariksa/compliance-policies/api';
import {
  DistributionByDocumentType,
  DataTypeInfo,
  DocumentClassificationForSourceInfo,
  FileMetaDataInfo,
  ObjectsDistributionCount,
  PageResourcesRead,
  RepositoryBranchesInfo,
  SourceCountSummary,
} from '@ariksa/data-scanning/api';
import { NativeResources } from '@ariksa/inventory-core';
import {
  AccessByIdentityPage,
  AppSchemasIacIacDeploymentSummary,
  Connection,
  ActionsResponse,
  DeployedResource,
  InventoryResourceBase,
  RiskContextResponse,
  SearchResponse,
} from '@ariksa/inventory-core/api';
import { AlertSeverityInitializer } from '@ariksa/notification';
import {
  AlertLogDetails,
  AlertResponse,
  BlueprintDetails,
  EntityTimelineResponse,
} from '@ariksa/notification/api';
import { IPagedQueryState } from 'services/utils/PagedQueryState';
import { IQueryState } from 'services/utils/QueryState';
import {
  ContainerImageMetadataRead,
  ContainerInfo,
  ECRImageVisibility,
  InstanceSummary,
  LibraryType,
  Vulnerability,
} from 'services/Vulnerability/types';
import { Dict } from 'types/utils';

export interface ActiveResourceState {
  resourceType: NativeResources;
  resourceUuid: string;
  resourceAccountId: string;
  resourceId: string;
  resourceArn?: string;
  searchTerm: string;
  cloudProvider?: CloudProviders;
  alerts: IPagedQueryState<AlertResponse[]>;
  resourceDetails: IQueryState<Dict<any>>; // resource by id
  resourceInsight: IQueryState<Dict<any>>;
  metadataSummary: IQueryState<Dict<any>>;
  riskContext: IQueryState<RiskContextResponse[]>;
  entityTimeline: IQueryState<EntityTimelineResponse[]>;
  activeTabIndex: number;
  activeTabName: string;
  vulnerabilitySummary: IQueryState<InstanceSummary>;
  vulnerabilityDetails: {
    os: Dict<IPagedQueryState<Vulnerability[]>>;
    application: Dict<IPagedQueryState<Vulnerability[]>>;
    library: Dict<IPagedQueryState<Vulnerability[]>>;
  };
  imageVulnerabilities: Dict<IPagedQueryState<Vulnerability[]>>;
  instanceVulnerabilityDetails: IQueryState<Dict<VulnerabilityDetails>>;
  amiHosts: {
    hosts: IPagedQueryState<any[]>;
    riskContext: IQueryState<Record<string, RiskContextResponse[]>>;
    vulnerabilities: Record<string, IQueryState<any>>;
  };

  resourceSeverity: IQueryState<AlertSeverityInitializer>;

  network: {
    openPorts: IQueryState<any>;
  };

  networkMap: IQueryState<any>;
  networkMapScale: number;
  piiByBucketName: IPagedQueryState<FileMetaDataInfo[]>;
  dataRiskContext: IQueryState<any>;
  canAccessSummary: {
    canAccess: IQueryState<Dict<any>[]>;
    accessByIdentity: IQueryState<Dict<any>[]>;
    // The paged Principals tab: one page with its filter counts.
    accessByIdentityPage: IQueryState<AccessByIdentityPage>;
    canAccessData: IQueryState<Dict<any>[]>;
    privilegeSummary: IQueryState<Dict<any>[]>;
    accessMap: IQueryState<SearchResponse>;
    expandedAccessMap: IQueryState<SearchResponse>;
  };
  data: {
    dataTypesBySource: IQueryState<DataTypeInfo[]>;
    resourcesByGroup: IQueryState<PageResourcesRead>;
    documentTypesBySource: IPagedQueryState<
      DocumentClassificationForSourceInfo[]
    >;
    summary: IQueryState<SourceCountSummary>;
    dataLabels: IQueryState<Record<string, number>>;
    typesOfFiles: IQueryState<ObjectsDistributionCount>;
    distributionByDocumentType: IQueryState<DistributionByDocumentType[]>;
  };
  permissions: {
    insecureAcl: IQueryState<S3InsecureACL[]>;
  };
  activity: {
    s3InternetActivity: IQueryState<S3InternetActivity[][]>;
    s3CrossAccount: IQueryState<S3CrossAccountAccess[]>;
  };

  instanceSummaryByID: IQueryState<Record<string, InstanceSummary>>;
  containerVulnerability: IQueryState<ContainerInfo[]>;

  linkedResourceDetails: IQueryState<Dict<any>>;

  ecrImages: {
    ecrContainerImages: IPagedQueryState<ECRImageVisibility[]>;
    ecrImageContainers: IQueryState<ContainerInfo[]>;
    containerRiskContext: IQueryState<Record<string, RiskContextResponse[]>>;
    imageMetadata: IQueryState<ContainerImageMetadataRead>;
  };
  branches: IQueryState<RepositoryBranchesInfo[]>;
  alertStatus: IQueryState<Dict<AlertLogDetails[]>>;
  alertAction: IQueryState<any>;
  accessed: IQueryState<V1ActionsPerformedMonthlyResponse>;
  actionsOnDay: IQueryState<V1ActionsPerformedOnDayResponse>;
  libraryType?: LibraryType;
  showResolvedVulnerabilities?: boolean;
  vulnerabilityPublishedTime: Record<string, any>;
  vulnerabilitySeverity: string;
  isPatchable: string;

  deploymentSummary: IQueryState<AppSchemasIacIacDeploymentSummary[]>;
  accountDeployedResources: Record<
    string,
    IPagedQueryState<DeployedResource[]>
  >;
  permissionsMap: IQueryState<ActionsResponse>;
  resourceActionsMonthly: IQueryState<V1ResourceActionsMonthlyResponse>;
  resourceActionsDaily: IQueryState<V1ResourceActionsDailyResponse>;

  runningProcesses: IPagedQueryState<any>;
  processConnections: Dict<IPagedQueryState<Connection[]>>;
  rules: IPagedQueryState<Record<string, any>[]>;
  resourceGroup: {
    resources: IPagedQueryState<InventoryResourceBase[]>;
    riskContext: IQueryState<Record<string, RiskContextResponse[]>>;
    alertCount: IQueryState<Record<string, number>>;
  };
  alertBlueprints: IQueryState<BlueprintDetails[]>;
  downloadReport: IQueryState<any>;
  tickets: IQueryState<any>;

  cveHosts: IQueryState<number>;
  cveAdditionalDetails: IQueryState<Record<string, any>>;
}

export interface VulnerabilityDetails {
  os: Vulnerability[];
  application: Vulnerability[];
  library: Vulnerability[];
}

export type ContainerState = ActiveResourceState;

export type ActiveResourceInfo = {
  resourceType: NativeResources;
  name?: string;
  uuid?: string;
  accountId: string;
  resourceId?: string;
  resourceArn?: string;
  tabIndex?: number;
  cloudProvider?: string; //CloudProviders
  showResolvedVulnerabilities?: boolean;
  vulnerabilityPublishedTime?: Record<string, any>;
  vulnerabilitySeverity?: string;
  isPatchable?: string;
  searchTerm?: string;
  tab?: string;
};

export const MetadataTabs = {
  Apps: 'Apps',
  Alerts: 'Issues',
  Activity: 'Activity',
  Access: 'Access',
  AccessPolicies: 'Access Policies',
  Compliance: 'Compliance',
  Containers: 'Containers',
  Principal: 'Principal',
  Summary: 'Summary',
  Secrets: 'Secrets',
  Network: 'Network',
  Permissions: 'Permissions',
  Data: 'Data',
  ContainerImages: 'Container Images',
  Timeline: 'Timeline',
  Vulnerabilities: 'Vulnerabilities',
  Deployment: 'Service',
  Hosts: 'Hosts',
  Resources: 'Resources',
  DataSources: 'Data Sources',
};
